<template>
  <metainfo>
    <template v-slot:title="{ content }">{{
      content ? `${content} | JuraNews` : `JuraNews`
    }}</template>
  </metainfo>
  <ion-app>
    <ion-split-pane content-id="main">
      <ion-menu side="start" content-id="main" type="overlay">
        <ion-header>
          <ion-toolbar>
            <ion-title>Menu</ion-title>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          <ion-list id="menu-list">
            <ion-menu-toggle auto-hide="false" v-for="(p, i) in menuItems" :key="i">
              <ion-item v-if="p && p.visible" lines="none" @click="action(p.routePath, p.event)" class="fas fa-users">
                <ion-icon slot="start" :name="p.icon" v-if="p.icon"></ion-icon>
                <fa style="font-size: 24px; opacity: 0.6" slot="start" class="icon-inner ionicon s-ion-icon"
                  :icon="p.faIcon" v-if="p.faIcon"></fa>
                <ion-label class="pl-0 pl-0">{{ p.name }}</ion-label>
              </ion-item>
              <ion-item-divider v-if="p.divider" />
            </ion-menu-toggle>
          </ion-list>
        </ion-content>
        <ion-row>
          <ion-col size="1" />
          <ion-col size="5">
            <ion-text color="secondary text-right pl-10 ion-text-start">
              <p>v{{ version }}{{ environment }}</p>
            </ion-text>
          </ion-col>
          <ion-col size="6" />
        </ion-row>
      </ion-menu>
      <ion-router-outlet id="main">
        <keep-alive include="ChatComponent">
          <router-view v-slot="{ Component }">
            <component :is="Component" />
          </router-view>
        </keep-alive>
      </ion-router-outlet>
    </ion-split-pane>
  </ion-app>
</template>

<script lang="ts">
import {
  IonApp,
  IonContent,
  IonList,
  IonMenu,
  IonRouterOutlet,
  IonSplitPane,
  IonMenuToggle,
  IonIcon,
  IonLabel,
  IonTitle,
  IonHeader,
  IonItem,
  IonToolbar,
  IonItemDivider,
  IonText,
  IonCol,
  IonRow,
  popoverController,
} from "@ionic/vue";
import {
  logOut,
  home,
  newspaper,
  layers,
  logIn,
  library,
  timer,
  book,
  notifications,
  documents,
  film,
  sparkles
} from "ionicons/icons";
import { useRouter } from "vue-router";
import { defineComponent, ref, onMounted, onBeforeUnmount, watch } from "vue";
import { addIcons } from "ionicons";
import { useCookie } from "@vue-composable/cookie";
import { Menu } from "@/models/menu.model";
import * as eventBus from "@/services/event.bus.js";
import { useStore } from "vuex";
import { isAndroid, isIOS, osVersion } from "mobile-device-detect";
import { JnService } from "@/services/jn.service";

addIcons({
  newspaper,
  "log-out": logOut,
  home,
  layers,
  "log-in": logIn,
  library,
  timer,
  book,
  notifications,
  documents,
  film,
  sparkles
});

export default defineComponent({
  inheritAttrs: false,
  name: "App",
  components: {
    IonApp,
    IonContent,
    IonList,
    IonMenu,
    IonRouterOutlet,
    IonSplitPane,
    IonMenuToggle,
    IonIcon,
    IonLabel,
    IonTitle,
    IonHeader,
    IonItem,
    IonToolbar,
    IonItemDivider,
    IonText,
    IonCol,
    IonRow,
  },
  setup() {
    const usernameCookie = useCookie("username");
    const paywallCookie = useCookie("paywall");
    const idSessionCookie = useCookie("SESSIONID");

    const username = ref("");
    const idSession = ref("");

    const isAuthenticated = ref(false);

    const version = ref(process.env.VUE_APP_VERSION);
    const environment = ref(process.env.VUE_APP_ENVIRONMENT);

    const router = useRouter()

    function isIosCompliant(): boolean {
      let isIosCompliant = true
      if (isIOS) {
        const major = Number(osVersion.split(".")[0])
        const minor = Number(osVersion.split(".")[1])
        const fix = Number(osVersion.split(".")[2])
        if (major < 16) {
          isIosCompliant = false
        }
        if (major == 16) {
          isIosCompliant = isIosCompliant && minor >= 4 && fix >= 1
        }
        if (major > 16) {
          isIosCompliant = true
        }
      }
      return isIosCompliant
    }

    const homeMenu = ref(
      new Menu({
        name: "Home",
        routeName: "Home",
        routePath: "/main",
        icon: "home",
        divider: false,
        visible: true,
      })
    );
    const newsMenu = ref(
      new Menu({
        name: "News",
        routeName: "News",
        routePath: "/news",
        icon: "newspaper",
        divider: false,
        visible: true,
      })
    );
    const areeTematicheMenu = ref(
      new Menu({
        name: "Aree Tematiche",
        routeName: "AreeTematiche",
        routePath: "/main/aree-tematiche",
        icon: "layers",
        divider: false,
        visible: true,
      })
    );
    const massimarioMenu = ref(
      new Menu({
        name: "Massimario",
        routeName: "Massimario",
        routePath: "/massimario",
        faIcon: "gavel",
        divider: false,
        visible: true,
      })
    );
    const formularioMenu = ref(
      new Menu({
        name: "Formulari",
        routeName: "Formulario",
        routePath: "/formulario",
        faIcon: "list",
        divider: false,
        visible: true,
      })
    );
    const realTimeMenu = ref(
      new Menu({
        name: "Real Time",
        routeName: "RealTime",
        routePath: "/main/real-time",
        icon: "timer",
        divider: false,
        visible: true,
      })
    );
    const bancaDatiMenu = ref(
      new Menu({
        name: "Banca Dati",
        routeName: "BancaDati",
        routePath: "/main/banca-dati",
        icon: "library",
        divider: false,
        visible: true,
      })
    );
    const codiciMenu = ref(
      new Menu({
        name: "Codici",
        routeName: "Codici",
        routePath: "/codici",
        icon: "book",
        divider: false,
        visible: true,
      })
    );
    const videoMenu = ref(
      new Menu({
        name: "Video",
        routeName: "Video",
        routePath: "/video",
        icon: "film",
        divider: true,
        visible: true,
      })
    );
    const aiChatMenu = ref(
      new Menu({
        name: "Chat AI",
        routeName: "ChatAI",
        routePath: "/chat-ai",
        icon: "sparkles",
        divider: false,
        visible: true,
      })
    );
    const dossierMenu = ref(
      new Menu({
        name: "Fascicoli",
        routeName: "Fascicoli",
        routePath: "/dossier",
        icon: "documents",
        divider: false,
        visible: false,
      })
    );
    const notificheMenu = ref(
      new Menu({
        name: "Notifiche Push",
        routeName: "Notifiche",
        routePath: "/notifiche",
        icon: "notifications",
        divider: false,
        visible: true,
      })
    );
    const loginMenu = ref(
      new Menu({
        name: "Login",
        routeName: "Login",
        routePath: "/login",
        icon: "log-in",
        divider: false,
        visible: true,
      })
    );
    const logoutMenu = ref(
      new Menu({
        name: "Logout",
        routeName: "Logout",
        event: "logout",
        icon: "layers",
        divider: false,
        visible: false,
      })
    );
    const menuItems = ref([
      homeMenu.value,
      newsMenu.value,
      areeTematicheMenu.value,
      massimarioMenu.value,
      formularioMenu.value,
      realTimeMenu.value,
      bancaDatiMenu.value,
      codiciMenu.value,
      aiChatMenu.value,
      videoMenu.value,
      dossierMenu.value,
    ]);

    if (isAndroid || isIosCompliant()) {
      menuItems.value.push(notificheMenu.value);
    }
    menuItems.value.push(loginMenu.value);
    menuItems.value.push(logoutMenu.value);

    aiChatMenu.value.visible = false;

    watch(isAuthenticated, (authenticated: boolean) => {
      loginMenu.value.visible = !authenticated;
      logoutMenu.value.visible = authenticated;
      dossierMenu.value.visible = authenticated;
      aiChatMenu.value.visible = authenticated && username.value == "mitmous82@gmail.com";
      notificheMenu.value.visible = authenticated;
      menuItems.value = [
        homeMenu.value,
        newsMenu.value,
        areeTematicheMenu.value,
        massimarioMenu.value,
        formularioMenu.value,
        realTimeMenu.value,
        bancaDatiMenu.value,
        codiciMenu.value,
        aiChatMenu.value,
        videoMenu.value,
        dossierMenu.value,
      ];
      if (isAndroid || isIosCompliant()) {
        menuItems.value.push(notificheMenu.value);
      }
      menuItems.value.push(loginMenu.value);
      menuItems.value.push(logoutMenu.value);
    });

    function action(to: string, event: string) {
      if (to) {
        //location.href = to;
        router.push(to);
      } else if (event) {
        eventBus.default.$emit(event);
      }
    }

    function findAnchorInEvent(ev: Event): HTMLAnchorElement | null {
      const e = ev as MouseEvent & { composedPath?: () => EventTarget[]; path?: EventTarget[] }
      // 1) prefer composedPath() (pierce shadow DOM)
      const path = (typeof e.composedPath === 'function')
        ? e.composedPath()
        : (e as any).path || []

      for (const node of path) {
        try {
          if (!node || !(node as any).tagName) continue
          const el = node as HTMLElement
          if (el.tagName.toLowerCase() === 'a') return el as HTMLAnchorElement
          // sometimes anchor is wrapped - try closest on element (non-shadow)
          const maybe = (el as HTMLElement).closest ? (el as HTMLElement).closest('a') : null
          if (maybe) return maybe as HTMLAnchorElement
        } catch (_) { continue }
      }

      // fallback: classic closest from event.target (if not shadow)
      const target = ev.target as HTMLElement | null
      return target ? target.closest('a') : null
    }

    function isInternalHref(href: string | null) {
      if (!href) return false
      const appHost = (process.env.VUE_APP_JURANEWS_APP_BASE_URL || 'https://app.juranews.it').replace(/\/+$/, '')
      // consider relative paths internal
      if (href.startsWith('/')) return true
      // absolute pointing to our host
      try {
        const u = new URL(href, window.location.origin)
        return u.origin === new URL(appHost).origin
      } catch {
        return false
      }
    }

    onMounted(async () => {
      eventBus.default.$on("logout", async function () {
        const idSess = idSession.value != "" ? idSession.value : useCookie("SESSIONID").cookie.value;
        const user = username.value != "" ? username.value : useCookie("username").cookie.value;
        if (user && idSess) {
          await JnService.logout(user, idSess!, Number(process.env.VUE_APP_PRODUCT_ID))
        }
        paywallCookie.removeCookie();
        usernameCookie.removeCookie();
        idSessionCookie.removeCookie();
        // store.commit('crmInfo', undefined);
        isAuthenticated.value = false;
        eventBus.default.$emit("paywall", true);
        // paywallCookie.setCookie('true');
        //  router.push('/main/home');
        dossierMenu.value.visible = false;
        aiChatMenu.value.visible = false;
      });

      eventBus.default.$on("login", function (authenticated: boolean, user: string, idSess: string) {     
        username.value = user;
        idSession.value = idSess;
        isAuthenticated.value = authenticated;
        dossierMenu.value.visible = authenticated;
        notificheMenu.value.visible = authenticated;
        aiChatMenu.value.visible = authenticated && user == "mitmous82@gmail.com";
      });

      const { cookie } = useCookie("username");
      if (cookie.value) {
        isAuthenticated.value = true;
        dossierMenu.value.visible = true;
        notificheMenu.value.visible = true;
        aiChatMenu.value.visible = cookie.value == "mitmous82@gmail.com";
      } else {
        isAuthenticated.value = false;
        dossierMenu.value.visible = false;
        notificheMenu.value.visible = false;
        aiChatMenu.value.visible = false;
      }

      const handler = (ev: Event) => {
          // only intercept left-clicks without modifier
          const me = ev as MouseEvent
          if (me.button && me.button !== 0) return
          if (me.metaKey || me.ctrlKey || me.shiftKey || me.altKey) return

          const anchor = findAnchorInEvent(ev)
          if (!anchor) return

          const href = anchor.getAttribute('href')
          const target = anchor.getAttribute('target')
          if (!href) return
          // do nothing on external links / new-tab anchors
          if (target === '_blank') return
          // don't intercept mailto:, tel:, javascript:
          if (/^(mailto:|tel:|javascript:)/i.test(href)) return

          // if not internal, open in new tab
          if (!isInternalHref(href)) {
            window.open(href, '_blank')
            ev.preventDefault()
            return
          }

          // convert absolute internal to relative path
          const base = (process.env.VUE_APP_JURANEWS_APP_BASE_URL || 'https://app.juranews.it').replace(/\/+$/, '')
          let relative = href
          if (href.startsWith(base)) {
            relative = href.slice(base.length) || '/'
          } else {
            // handle fully qualified with same origin
            try {
              const u = new URL(href, window.location.origin)
              relative = u.pathname + u.search + u.hash
            } catch {}
          }

          // prevent actual navigation and use router
          ev.preventDefault()
          // optionally: normalize repeated slashes
          relative = relative.replace(/^\/+/, '/')
          router.push(relative).catch((err) => {
            // ignore NavigationDuplicated
            if (err && err.name !== 'NavigationDuplicated') console.error(err)
          })
        }

        document.addEventListener('click', handler, { capture: true })
        onBeforeUnmount(() => document.removeEventListener('click', handler, { capture: true }))
    });


    return {
      menuItems,
      action,
      isAuthenticated,
      version,
      environment,
    };
  },
});
</script>